import { motion } from "framer-motion";
import { HERO } from "@/constants/testIds";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import TrustBadges from "@/components/TrustBadges";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

// Split headline into words for stagger animation
const HEADLINE_LINES = [
  ["Websites"],
  ["That", "Turn"],
  ["Visitors", "Into"],
  ["Customers."],
];

export default function Hero() {
  return (
    <section
      id="home"
      data-testid={HERO.root}
      className="relative min-h-[100svh] pt-32 pb-16 lg:pb-24 overflow-hidden"
    >
      {/* Background texture */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.18] pointer-events-none"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/9665190/pexels-photo-9665190.jpeg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          mixBlendMode: "screen",
          filter: "grayscale(1) contrast(1.1)",
        }}
      />
      {/* Vignette gradient */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 50% 40%, transparent 0%, rgba(5,5,5,0.6) 60%, #050505 100%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
        {/* Overline */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.6 }}
          className="flex items-center gap-3 font-mono-tag text-xs text-white/60 mb-8 lg:mb-12"
        >
          <span className="w-2 h-2 rounded-full bg-[#10B981] pulse-dot" />
          Available for new work · Q1 2026 · Based in the UAE
        </motion.div>

        {/* Headline — kinetic per-word reveal */}
        <h1
          data-testid={HERO.headline}
          className="font-display font-medium text-[3.2rem] sm:text-[4.5rem] md:text-[6rem] lg:text-[8rem] xl:text-[9.5rem] leading-[0.9]"
        >
          {HEADLINE_LINES.map((line, li) => (
            <span key={li} className="block overflow-hidden">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{
                  duration: 0.95,
                  delay: 1.7 + li * 0.11,
                  ease: [0.16, 1, 0.3, 1],
                }}
                className="inline-block"
              >
                {line.map((word, wi) => (
                  <span key={wi} className="inline-block mr-[0.25em]">
                    {li === 3 ? (
                      <span className="italic font-light text-[#E2FF31]">
                        {word}
                      </span>
                    ) : (
                      word
                    )}
                  </span>
                ))}
              </motion.span>
            </span>
          ))}
        </h1>

        {/* Sub + CTAs */}
        <div className="mt-10 lg:mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <motion.p
            data-testid={HERO.subheadline}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 2.35 }}
            className="lg:col-span-6 text-white/70 text-base sm:text-lg leading-relaxed max-w-xl"
          >
            <span className="text-white font-medium">Elevate Studios</span>{" "}
            designs and builds premium websites for founders and small brands —
            optimised to convert visitors into paying customers. React on the
            front, FastAPI on the back, every pixel deliberate.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 2.5 }}
            className="lg:col-span-6 lg:justify-self-end flex flex-wrap gap-3"
          >
            <button
              data-testid={HERO.ctaContact}
              data-cursor="Get quote"
              onClick={() => scrollTo("contact")}
              className="btn-primary"
            >
              Get a free quote <span className="arrow">→</span>
            </button>
            <button
              data-testid={HERO.ctaWork}
              data-cursor="See work"
              onClick={() => scrollTo("work")}
              className="btn-ghost"
            >
              See selected work <ArrowUpRight size={16} />
            </button>
          </motion.div>
        </div>

        {/* Trust badges */}
        <TrustBadges />

        {/* Scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.9, duration: 0.6 }}
          className="mt-14 lg:mt-20 flex items-center gap-3 text-white/40 font-mono-tag text-[10px]"
        >
          <ArrowDown size={14} className="animate-bounce" /> Scroll to see three
          real conversion-ready sites
        </motion.div>
      </div>
    </section>
  );
}
